import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSession } from "@/hooks/use-session";
import { IntegrationSettings } from "@/components/fundraising/IntegrationSettings";
import { ComplianceRulesList } from "@/components/fundraising/ComplianceRulesList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type FundraisingSettingsSection = "integrations" | "compliance";

export const Route = createFileRoute("/_app/_settings/fundraising-settings")({
	component: FundraisingSettingsPage,
	validateSearch: (search: Record<string, unknown>) => {
		const valid: FundraisingSettingsSection[] = ["integrations", "compliance"];
		const section = valid.includes(search.section as FundraisingSettingsSection)
			? (search.section as FundraisingSettingsSection)
			: "integrations";
		return { section };
	},
});

function FundraisingSettingsPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { section } = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });

	if (!campaignId) {
		return (
			<div className="p-6 text-muted-foreground">
				No campaign selected.
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-3xl space-y-6 p-6">
			<Tabs
				value={section}
				onValueChange={(value) =>
					navigate({
						search: { section: value as FundraisingSettingsSection },
						replace: true,
					})
				}
			>
				<TabsList>
					<TabsTrigger value="integrations">Integrations</TabsTrigger>
					<TabsTrigger value="compliance">Compliance</TabsTrigger>
				</TabsList>

				<TabsContent value="integrations" className="mt-6">
					<IntegrationSettings campaignId={campaignId} />
				</TabsContent>

				<TabsContent value="compliance" className="mt-6 space-y-6">
					<div>
						<h2 className="text-lg font-semibold">Compliance Rules</h2>
						<p className="text-sm text-muted-foreground">
							Configure contribution limits and prohibited source rules
							for your race
						</p>
					</div>
					<ComplianceRulesList campaignId={campaignId} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
